'use client';
import React, { FC, useEffect } from 'react';
import { useSearchParams } from 'next/navigation';
import { useRouter, usePathname } from '@/navigation';
import { PREVIEW_USER_UUID_KEY } from '@/utils/adminPreviewModeHelpers';

export type AdminPreviewBannerProps = {
  adminName: string | undefined;
  userEmail: string | undefined;
  onAdminPreviewExit?: () => Promise<void>;
  onAdminPreviewEnter?: (previewUserUuid: string) => Promise<void>;
};

const AdminPreviewBanner: FC<AdminPreviewBannerProps> = ({
  adminName,
  userEmail,
  onAdminPreviewEnter,
}) => {
  const pathname = usePathname();
  const router = useRouter();
  const searchParams = useSearchParams();

  useEffect(() => {
    function onPreviewEnter() {
      if (searchParams?.get(PREVIEW_USER_UUID_KEY)) {
        const previewUserUuid = searchParams?.get(PREVIEW_USER_UUID_KEY);
        if (previewUserUuid) {
          if (onAdminPreviewEnter) {
            onAdminPreviewEnter(previewUserUuid).then(() => {
              const nextSearchParams = new URLSearchParams(
                searchParams?.toString(),
              );
              nextSearchParams.delete(PREVIEW_USER_UUID_KEY);

              router.replace(`${pathname}?${nextSearchParams}`, {
                scroll: false,
              });
            });
          }
        }
      }
    }

    onPreviewEnter();
  }, [pathname, router, searchParams, onAdminPreviewEnter]);

  if (!adminName) {
    return null;
  }

  return (
    <div
      data-testid="admin-preview-banner"
      className="flex flex-wrap items-center justify-between rounded-md bg-indigo-pastel p-5 mt-10"
    >
      {userEmail && (
        <p>
          Hey {adminName}, you're previewing{' '}
          <span className="font-bold">{userEmail}</span>'s account
        </p>
      )}
    </div>
  );
};

export default React.memo(AdminPreviewBanner);
